import parsePhoneNumber from "libphonenumber-js"
import { z } from "zod"

import { MerchantProfileCreateScalarSchema } from "@/lib/zenstack/zod/models/MerchantProfile.schema"
import { UseMerchantTranslationsType } from "../SchemaUtils"
import { CONST } from "@/constants"
export type TMerchantProfileCreateData = z.infer<ReturnType<typeof MerchantProfileSchemas.create>>
export type TMerchantProfileUpdateData = z.infer<ReturnType<typeof MerchantProfileSchemas.update>>

export class MerchantProfileSchemas {
  static create(t: UseMerchantTranslationsType) {
    return MerchantProfileCreateScalarSchema.omit({
      merchantType: true,
      defaultDiscount: true,
      phone: true,
      zip: true,
      merchantAreaEN: true,
      merchantAreaFR: true,
      merchantTypeEN: true,
      merchantTypeFR: true,
      descriptionEN: true,
      descriptionFR: true,
      latitude: true,
      longitude: true,
      website: true,
      embeddedMap: true,
    }).merge(
      z.object({
        phone: z
          .string()
          .min(10, t("profile.validation.phone.min"))
          .max(20, t("profile.validation.phone.max"))
          .superRefine((phone, ctx) => {
            const isValid = parsePhoneNumber(phone, "CA")?.isValid()
            if (!isValid) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t("profile.validation.phone.invalid"),
              })
            }
          }),
        zip: z
          .string()
          .min(5, t("profile.validation.zip.min"))
          .max(7, t("profile.validation.zip.max"))
          .superRefine((zip, ctx) => {
            const isValid = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i.test(zip)
            if (!isValid) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t("profile.validation.zip.invalid"),
              })
            }
          }),
        descriptionEN: z.string().max(1000, t("profile.validation.description.max")).optional(),
        descriptionFR: z.string().max(1000, t("profile.validation.description.max")).optional(),
        defaultDiscount: z
          .number({
            invalid_type_error: t("profile.validation.discount.invalid", {
              min: CONST.MIN_DISCOUNT,
              max: CONST.MAX_DISCOUNT,
            }),
          })
          .min(CONST.MIN_DISCOUNT, t("profile.validation.discount.min", { min: CONST.MIN_DISCOUNT }))
          .max(CONST.MAX_DISCOUNT, t("profile.validation.discount.max", { max: CONST.MAX_DISCOUNT }))
          .optional(),
        website: z
          .string()
          .regex(
            /^((https?):\/\/)?(www.)?[a-z0-9]+\.[a-z]+(\/[a-zA-Z0-9#]+\/?)*$/,
            t("profile.validation.website.invalid")
          )
          .optional(),
        weeklyCouponLimit: z
          .number({ invalid_type_error: t("profile.validation.weekly_coupon_limit.invalid") })
          .optional(),
      })
    )
  }

  static update(t: UseMerchantTranslationsType) {
    return MerchantProfileSchemas.create(t).partial().passthrough()
  }
}
